const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';

class APIService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    
    const defaultOptions = {
      headers: {
        'Content-Type': 'application/json',
      }
    };

    const mergedOptions = {
      ...defaultOptions,
      ...options,
      headers: {
        ...defaultOptions.headers,
        ...options.headers,
      },
    };

    try {
      const response = await fetch(url, mergedOptions);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  // Simulator endpoints
  async runSimulation(simulationData) {
    return this.request('/simulator/run', {
      method: 'POST',
      body: JSON.stringify(simulationData),
    });
  }

  async runBatchScenarios(scenariosData) {
    return this.request('/simulator/batch-scenarios', {
      method: 'POST',
      body: JSON.stringify(scenariosData),
    });
  }

  async getAIAnalysis(simulationResult) {
    return this.request('/simulator/ai-analysis', {
      method: 'POST',
      body: JSON.stringify(simulationResult),
    });
  }

  // Structured simulation endpoint (new improved version)
  async runStructuredSimulation(simulationData) {
    return this.request('/api/simulate/structured', {
      method: 'POST',
      body: JSON.stringify(simulationData),
    });
  }

  // FRED data endpoints
  async getFredData(series, startDate = null, endDate = null) {
    const params = new URLSearchParams({ series });
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    
    return this.request(`/fred/series?${params.toString()}`);
  }

  async getFredSummary(series, startDate = null, endDate = null) {
    const params = new URLSearchParams({ series });
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    
    return this.request(`/fred/summary?${params.toString()}`);
  }

  // Real-time rates
  async getCurrentRates() {
    return this.request('/current-rates');
  }

  // Historical data
  async getHistoricalData(period = '1Y') {
    return this.request(`/historical-rates?period=${period}`);
  }

  // Market forecast
  async getMarketForecast() {
    return this.request('/forecast');
  }

  // Loan qualification analysis
  async analyzeLoanQualification(qualificationData) {
    return this.request('/api/loan-qualification/analyze', {
      method: 'POST',
      body: JSON.stringify(qualificationData),
    });
  }

  // Market data endpoints
  async getComprehensiveMarketData(forceRefresh = false) {
    return this.request(`/api/market-data/comprehensive?force_refresh=${forceRefresh}`);
  }

  async getRateEnvironmentAnalysis() {
    return this.request('/api/market-data/environment-analysis');
  }

  async getFredTestData() {
    return this.request('/api/market-data/fred-test');
  }

  async getMndTestData() {
    return this.request('/api/market-data/mnd-test');
  }

  // Historical data endpoints
  async getHistoricalComparison(days = 30) {
    // Add timestamp to bust cache
    const timestamp = Date.now();
    return this.request(`/api/historical-comparison?days=${days}&_t=${timestamp}`);
  }

  async getHistoricalTreasuryRates(seriesId = 'DGS10', daysBack = 30) {
    return this.request(`/api/historical/treasury?series_id=${seriesId}&days_back=${daysBack}`);
  }

  async getHistoricalMortgageRates(daysBack = 30) {
    return this.request(`/api/historical/mortgage?days_back=${daysBack}`);
  }

  async getHistoricalFedFunds(daysBack = 30) {
    return this.request(`/api/historical/treasury?series_id=FEDFUNDS&days_back=${daysBack}`);
  }

  async getHistoricalInflation(daysBack = 30) {
    return this.request(`/api/historical/treasury?series_id=CPIAUCSL&days_back=${daysBack}`);
  }

  async getHistoricalUnemployment(daysBack = 30) {
    return this.request(`/api/historical/treasury?series_id=UNRATE&days_back=${daysBack}`);
  }

  // FOMC meeting calendar (scraped from federalreserve.gov)
  async getFomcCalendar() {
    return this.request('/api/fomc/calendar');
  }

  // Forecast endpoints
  async getSimpleForecast() {
    return this.request('/api/forecast/simple');
  }

  async getForecastData() {
    return this.request('/api/forecast');
  }

  async getRateTrend(days = 7) {
    return this.request(`/api/forecast/trend?days=${days}`);
  }

  async getRateDirection() {
    return this.request('/api/forecast/direction');
  }

  // Historical data endpoints
  async getHistoricalRates(period = '1month') {
    return this.request(`/api/historical?period=${period}`);
  }

  async getEnhancedHistoricalData(period = '1month') {
    return this.request(`/api/historical/enhanced?period=${period}`);
  }

  // AI Analysis endpoints (OpenAI-powered)
  async getAIMarketAnalysis(forceRefresh = false) {
    return this.request(`/api/ai-analysis/market-conditions?force_refresh=${forceRefresh}`);
  }

  async getAIRatePredictions(forceRefresh = false) {
    return this.request(`/api/ai-analysis/rate-predictions?force_refresh=${forceRefresh}`);
  }

  async getAIForecastCommentary() {
    return this.request('/api/ai-analysis/forecast-commentary');
  }

  async getAIRateExplanation() {
    return this.request('/api/ai-analysis/rate-explanation');
  }

  async getAIBorrowerRecommendations(scenario) {
    return this.request('/api/ai-analysis/borrower-recommendations', {
      method: 'POST',
      body: JSON.stringify(scenario),
    });
  }

  async getComprehensiveEnhancedAnalysis() {
    return this.request('/api/enhanced-analysis/comprehensive');
  }

  async getEnhancedAPIStatus() {
    return this.request('/api/enhanced-analysis/api-status');
  }

  // Get historical trend graph data
  async getHistoricalTrendGraph(indicator, period = '1month') {
    const days = period === '1week' ? 7 :
               period === '1month' ? 30 : 
               period === '3months' ? 90 : 
               period === '6months' ? 180 : 
               period === '1year' ? 365 : 30;
    
    // Try multiple endpoints for graph data
    const endpoints = [
      `/api/historical/trends?days_back=${days}&indicator=${indicator}`,
      `/api/historical/rates/range?days=${days}&series=${this.mapIndicatorToSeries(indicator)}`,
      `/api/fred/series?series=${this.mapIndicatorToFredSeries(indicator)}&days=${days}`,
      `/api/historical/enhanced/chart?period=${period}&indicator=${indicator}`
    ];
    
    let lastError = null;
    for (const endpoint of endpoints) {
      try {
        console.log(`Trying trend graph endpoint: ${endpoint}`);
        const response = await this.request(endpoint);
        console.log(`Success with trend endpoint: ${endpoint}`, response);
        return response;
      } catch (err) {
        console.warn(`Trend graph endpoint ${endpoint} failed:`, err);
        lastError = err;
      }
    }
    
    // All endpoints failed
    throw lastError || new Error('Failed to fetch historical trend graph data');
  }

  // Map indicator keys to appropriate series for different APIs
  mapIndicatorToSeries(indicator) {
    const mapping = {
      'treasury_10y': 'treasury10',
      'treasury_2y': 'treasury2',
      'fed_funds_rate': 'fedfunds',
      'mortgage_rate_30y': 'mortgage',
      'unemployment_rate': 'unemployment',
      'cpi_inflation': 'inflation',
    };
    return mapping[indicator] || indicator;
  }

  mapIndicatorToFredSeries(indicator) {
    const mapping = {
      'treasury_10y': 'DGS10',
      'treasury_2y': 'DGS2',
      'fed_funds_rate': 'FEDFUNDS',
      'mortgage_rate_30y': 'MORTGAGE30US',
      'unemployment_rate': 'UNRATE',
      'cpi_inflation': 'CPIAUCSL',
    };
    return mapping[indicator] || indicator;
  }
}

export const apiService = new APIService();
export default APIService;
